import { Redirect, router } from "expo-router";
import * as WebBrowser from "expo-web-browser";
import { observer } from "mobx-react";
import { FormattedMessage } from "react-intl";
import { Image, Linking } from "react-native";
import { Layout } from "~/components/Layout";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import { withAuthData } from "~/hoc/withAuthData";
import { useClinicPhoneNumber } from "~/hooks/useClinicPhoneNumber";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { getPhoneNumberWithCountryCode } from "~/utils/getPhoneNumberWithCountryCode";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { BankIdLoginButton } from "../login/BankIdLoginButton";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import type * as AuthSession from "expo-auth-session";

interface StandaloneBookingLoginScreenProps {
  authRequest: AuthSession.AuthRequest;
  discovery: AuthSession.DiscoveryDocument;
}

WebBrowser.maybeCompleteAuthSession();

export const LoginScreenWithAuthDataWithAuthData = withAuthData(
  observer(function LoginScreenWithAuthData({
    discovery,
    authRequest,
  }: StandaloneBookingLoginScreenProps) {
    const errorAlert = useErrorAlertV2();
    const store = useStore();
    const { clinicId, isStandAloneBookingFlow } = useStandaloneBookingFlow();
    const { clinicPhoneNumber, clinicPhoneNumberCountryCode } =
      useClinicPhoneNumber();

    const imageWidth = 135.21;
    const aspectRatio = 3116 / 1479;
    const imageHeight = imageWidth / aspectRatio;

    if (!isStandAloneBookingFlow) {
      return <Redirect href={"not-found" as any} />;
    }

    return (
      <Screen withBottomInset withTopInset={false}>
        <Layout
          cardStyle={{
            backgroundColor: styleConstantsV2.colorPrimary,
            paddingHorizontal: styleConstantsV2.spacing24,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            borderBottomLeftRadius: 24,
            borderBottomRightRadius: 24,
          }}
          mobileContainerStyle={{
            backgroundColor: styleConstantsV2.colorPrimary,
            paddingHorizontal: styleConstantsV2.spacing24,
          }}
        >
          <View
            flex
            centerContent
            style={{
              paddingTop: 112,
            }}
          >
            <Image
              source={require("~/features/splash/evidensia-logo.png")}
              style={{ width: imageWidth, height: imageHeight }}
            />
            <Spacer extraExtraLarge />

            <BrandText sizeH1 colorWhite weightExtraBold alignCenter>
              <FormattedMessage
                id="POBzNN"
                defaultMessage="Log in to book an appointment."
                description="Title for standalone booking login screen"
              />
            </BrandText>

            <Spacer small />

            <BodyText colorWhite alignCenter sizeExtraLarge weightRegular>
              <FormattedMessage
                id="njiUl3"
                defaultMessage="This way, we can see if you’re a new or returning customer and tailor your booking experience accordingly."
                description="Description for standalone booking login screen"
              />
            </BodyText>

            <Spacer extraLarge />
            <Spacer small />

            <BankIdLoginButton
              discovery={discovery}
              authRequest={authRequest}
              onError={(error) => {
                if (error === "DUPLICATE_USER_FOUND") {
                  errorAlert(
                    new Error("Duplicate user found"),
                    intl.formatMessage({
                      id: "rIfma1",
                      defaultMessage: "Login failed",
                      description:
                        "Title for error message when duplicate user is found",
                    }),
                    intl.formatMessage({
                      id: "/yc/w7",
                      defaultMessage:
                        "We have detected that multiple profiles are linked to your personal identification number, which prevents you from booking visits online.",
                      description:
                        "Description for error message when duplicate user is found",
                    }),
                    intl.formatMessage({
                      id: "Hp/8ql",
                      defaultMessage: "Ok close",
                      description: "Button text for error message",
                    }),
                    undefined,
                    true,
                  );
                }

                return;
              }}
              onLogin={(user) => {
                // Log the event to the booking data layer
                bookingDataLayerPush({
                  event: "bankid_identify_success_web_app",
                });

                const isNewUser = user === undefined;

                if (isNewUser) {
                  // Booking flow for new user has 1 extra step (add client details)
                  store.bookingStore.setTotalProgressBarSteps(8);

                  // Clear active user to ensure the user is not set from previous session
                  store.authStore.clearActiveUser();

                  router.replace({
                    pathname: "/(standalone)/booking",
                    params: {
                      clinicId,
                      isNewUser: "1",
                    },
                  });
                } else {
                  store.bookingStore.setTotalProgressBarSteps(7);

                  router.replace({
                    pathname: "/(standalone)/booking",
                    params: {
                      clinicId,
                      isNewUser: "0",
                    },
                  });
                }
              }}
            />
            {!!clinicPhoneNumber && !!clinicPhoneNumberCountryCode && (
              <View
                flexDirectionColumnReverse
                flex
                style={{ paddingBottom: 64 }}
              >
                <BodyText alignCenter sizeExtraLarge weightRegular colorWhite>
                  <FormattedMessage
                    defaultMessage="Don’t have BankID?{br}Please contact us at <clinicPhoneNumberText>{clinicPhoneNumber}</clinicPhoneNumberText> to book an appointment."
                    id="4Bsy75"
                    description="Text informing the user to contact the clinic if they don't have BankID"
                    values={{
                      br: "\n",
                      clinicPhoneNumber: clinicPhoneNumber,
                      clinicPhoneNumberText: (chunks) => {
                        return (
                          <BodyText
                            alignCenter
                            sizeExtraLarge
                            weightRegular
                            colorWhite
                            underline
                            onPress={() => {
                              // Log the event to the booking data layer
                              bookingDataLayerPush({
                                event: "call_clinic_phone_web_app",
                                ga_action: "call_clinic_phone",
                                ga_label: "StandaloneBookingLoginScreen",
                              });

                              Linking.openURL(
                                `tel:${getPhoneNumberWithCountryCode(clinicPhoneNumberCountryCode, clinicPhoneNumber)}`,
                              );
                            }}
                          >
                            {chunks}
                          </BodyText>
                        );
                      },
                    }}
                  />
                </BodyText>
                <Spacer extraLarge />
              </View>
            )}
          </View>
        </Layout>
      </Screen>
    );
  }),
  `booking-login?clinicId=`,
);
